/**
 * Derived helpers built on top of PAGE_REGISTRY.
 * Every consumer (frontend configs, backend middleware, role model seed)
 * should call these instead of maintaining its own copy of page keys.
 */

import { PAGE_REGISTRY, MODULES, BASE_ACTIONS, GROUP_LABELS, GROUPS } from './registry.js';
import { isPageLicensedWithOverlay } from './custom-permission-overlay.js';

/** Role UI / Advanced auth: Definitions hub groups first (sidebar order), then others. */
const DEFINITIONS_GROUP_ORDER = [
    GROUPS.DEFINITIONS_ORGANIZATION,
    GROUPS.DEFINITIONS_RISK_CATALOG,
    GROUPS.DEFINITIONS_PERSONAL_DATA,
    GROUPS.DEFINITIONS_COMPLIANCE,
    GROUPS.DEFINITIONS_DOCUMENT,
    GROUPS.DEFINITIONS_PLATFORM,
    GROUPS.VENDOR_MANAGEMENT,
];

// ── Lookup indexes (built once at import time) ──────────────────────────────

const _byKey = new Map(PAGE_REGISTRY.map(p => [p.key, p]));

const _byModule = PAGE_REGISTRY.reduce((acc, p) => {
    (acc[p.module] ??= []).push(p);
    return acc;
}, {});

const _byGroup = PAGE_REGISTRY.reduce((acc, p) => {
    (acc[p.group] ??= []).push(p);
    return acc;
}, {});

// ── Single-page lookups ─────────────────────────────────────────────────────

export const getPageDef = (pageKey) =>
    pageKey == null || pageKey === ''
        ? null
        : (_byKey.get(String(pageKey).trim()) ?? PAGE_REGISTRY.find((p) => p.key === String(pageKey).trim()) ?? null);

export const getActionsForPage = (pageKey) =>
    getPageDef(pageKey)?.actions ?? [...BASE_ACTIONS];

export const getModuleForPage = (pageKey) =>
    getPageDef(pageKey)?.module ?? null;

// ── Collection queries ──────────────────────────────────────────────────────

export const getPagesByModule = (mod) => _byModule[mod] ?? [];

export const getPagesByGroup = (group) => _byGroup[group] ?? [];

export const getPagePermissionKeys = () => PAGE_REGISTRY.map(p => p.key);

export const getAllPageKeys = getPagePermissionKeys;

// ── Licensing helpers ───────────────────────────────────────────────────────

export const filterByLicensedModules = (licensedModules = []) =>
    PAGE_REGISTRY.filter(
        p => p.module === MODULES.CORE || licensedModules.includes(p.module),
    );

/**
 * Resolve page row for licensing. Prefer `_byKey` (fast); fall back to first
 * `PAGE_REGISTRY` row with matching `key` because `_byKey` is a Map and only
 * keeps one entry per key when the registry intentionally duplicates keys
 * (e.g. same pageKey + multiple paths with `excludeFromPathIndex`).
 */
function getPageDefForLicense(pageKey) {
    if (pageKey == null || pageKey === '') return null;
    const key = String(pageKey).trim();
    return _byKey.get(key) ?? PAGE_REGISTRY.find((p) => p.key === key) ?? null;
}

export const isPageLicensed = (pageKey, licensedModules = [], opts = {}) => {
    if (opts?.customDefsByPageKey) {
        return isPageLicensedWithOverlay(pageKey, licensedModules, opts);
    }
    const def = getPageDefForLicense(pageKey);
    if (!def) return false;
    const modules = Array.isArray(licensedModules) ? licensedModules : [];
    const candidates = Array.isArray(def.anyModule) && def.anyModule.length
        ? def.anyModule
        : [def.module];
    return candidates.some((mod) => {
        if (mod === MODULES.CORE || mod === 'core') return true;
        return modules.includes(mod);
    });
};

// ── Group helpers (replaces pageGroupsConfig.js) ────────────────────────────

export const getPageGroups = () => {
    const groups = {};
    for (const [groupKey, pages] of Object.entries(_byGroup)) {
        groups[groupKey] = {
            label: GROUP_LABELS[groupKey] ?? `permissions.groups.${groupKey}`,
            pages: pages.map(p => p.key),
        };
    }
    return groups;
};

export const getGroupOrder = () => {
    const keys = Object.keys(_byGroup);
    const priority = DEFINITIONS_GROUP_ORDER.filter((k) => keys.includes(k));
    const rest = keys.filter((k) => !DEFINITIONS_GROUP_ORDER.includes(k));
    return [...priority, ...rest];
};

export const getPageGroup = (pageKey) => {
    const def = _byKey.get(pageKey);
    return def?.group ?? 'other';
};

// ── Page-permissions config (replaces pagePermissionsConfig.js) ─────────────

export const getPagePermissions = () =>
    PAGE_REGISTRY.map(({ key, path }) => ({ key, path }));

// ── Available pages list (replaces role-options.js availablePages) ───────────

export const getAvailablePages = (licensedModules) => {
    const pages = licensedModules
        ? filterByLicensedModules(licensedModules)
        : PAGE_REGISTRY;
    return pages.map(p => p.key);
};

// ── SuperAdmin seed helper ──────────────────────────────────────────────────

export const buildSuperAdminAuthorization = (licensedModules) => {
    const pages = licensedModules
        ? filterByLicensedModules(licensedModules)
        : PAGE_REGISTRY;
    return pages.map(p => ({
        page: p.key,
        permissions: [...p.actions],
    }));
};

// ── Path ↔ key resolution ───────────────────────────────────────────────────

const _byPath = new Map(
    PAGE_REGISTRY
        .filter((p) => !p.excludeFromPathIndex)
        .flatMap((p) => [
            p.path,
            ...(Array.isArray(p.additionalPaths) ? p.additionalPaths : []),
        ].map((path) => [
            path.replace(/\/$/, ''),
            p,
        ])),
);

function registryPathMatchesActual(actualPath, registryPath) {
    const a = actualPath.split('/').filter(Boolean);
    const p = registryPath.split('/').filter(Boolean);
    if (a.length !== p.length) return false;
    for (let i = 0; i < p.length; i++) {
        const ps = p[i];
        const as = a[i];
        if (ps.startsWith(':')) continue;
        if (ps !== as) return false;
    }
    return true;
}

/** Registry path for a page key (no query/hash). */
export const getPagePathByKey = (pageKey) => {
    const def = getPageDef(pageKey);
    if (!def?.path) return undefined;
    return def.path.replace(/\/$/, '') || def.path;
};

export const getPageKeyByPath = (pathname) => {
    if (!pathname) return undefined;
    const clean = pathname.split('?')[0].split('#')[0].replace(/\/$/, '');
    if (clean === '/platform/assignment-templates') {
        return 'platformAssignmentTemplates';
    }
    if (clean === '/definitions/organization/teams' || clean === '/teams') {
        return 'definitionsGeneralSystemTeam';
    }
    if (clean === '/definitions/organization/duty-definitions' || clean.startsWith('/definitions/organization/duty-definitions/')) {
        return 'definitionsGeneralSystemDutyDefinition';
    }
    if (clean.startsWith('/definitions/organization/team/') && clean !== '/definitions/organization/team') {
        return 'definitionsGeneralSystemTeam';
    }
    if (clean.startsWith('/itsm/change-request/') && clean !== '/itsm/change-request') {
        return 'itsmChangeRequest';
    }
    if (clean.startsWith('/itsm/problem/') && clean !== '/itsm/problem') {
        return 'itsmProblem';
    }
    if (clean === '/ticketing/create' || clean === '/ticketing/my-tasks' || clean === '/ticketing/dashboard') {
        return 'ticketing';
    }
    // Historical ITSM URL — TicketSLA SoT lives on the ticketing (127) settings page.
    if (clean === '/itsm/sla-management') {
        return 'ticketSLA';
    }
    if (clean === '/incident-management/templates') {
        return 'incidentManagementIncidents';
    }
    const exact = _byPath.get(clean);
    if (exact) return exact.key;
    if (
        clean.startsWith('/vendor-management/vendors/') &&
        clean !== '/vendor-management/vendors'
    ) {
        const rest = clean.slice('/vendor-management/vendors/'.length);
        if (rest && !rest.includes('/') && rest.length >= 1) {
            return 'vendorManagementVendors';
        }
    }
    for (const p of PAGE_REGISTRY) {
        const regPath = p.path.replace(/\/$/, '');
        if (regPath.includes(':') && registryPathMatchesActual(clean, regPath)) {
            return p.key;
        }
    }
    return undefined;
};

/**
 * ITSM (se-128) page keys. Hub and `/itsm` APIs grant View if the user can
 * View any of these. `ticketing` / `ticketSLA` stay on the 127 boundary.
 */
export const ITSM_PAGE_KEYS = Object.freeze([
    'itsmUnifiedSearch',
    'itsmServiceCatalog',
    'itsmChangeRequest',
    'itsmProblem',
    'itsmSupplierContracts',
]);

/** 127 TicketSLA admin surface (settings tab). Not an ITSM SoT. */
export const TICKETING_SLA_SETTINGS_PATH = '/ticketing/settings?tab=sla';

/**
 * @param {(pageKey: string) => boolean} canView
 * @returns {boolean}
 */
export function canAccessItsmHub(canView) {
    return ITSM_PAGE_KEYS.some((key) => Boolean(canView?.(key)));
}

/**
 * Which unified-search / summary buckets the caller may see.
 * Tickets stay on 127 (`ticketing`). Problems/CRs also open with hub search.
 *
 * @param {(pageKey: string) => boolean} canView
 * @returns {{ tickets: boolean, incidents: boolean, problems: boolean, changeRequests: boolean }}
 */
export function resolveItsmHubSearchBuckets(canView) {
    const view = (pageKey) => Boolean(canView?.(pageKey));
    const unified = view('itsmUnifiedSearch');
    return {
        tickets: view('ticketing'),
        incidents: view('incidentManagementIncidents'),
        problems: unified || view('itsmProblem'),
        changeRequests: unified || view('itsmChangeRequest'),
    };
}

/**
 * @param {(pageKey: string) => boolean|Promise<boolean>} canView
 * @returns {Promise<{ tickets: boolean, incidents: boolean, problems: boolean, changeRequests: boolean }>}
 */
export async function resolveItsmHubSearchBucketsAsync(canView) {
    const keys = [
        'itsmUnifiedSearch',
        'ticketing',
        'incidentManagementIncidents',
        'itsmProblem',
        'itsmChangeRequest',
    ];
    const flags = await Promise.all(keys.map((key) => canView(key)));
    const map = Object.fromEntries(keys.map((key, i) => [key, Boolean(flags[i])]));
    return resolveItsmHubSearchBuckets((pageKey) => map[pageKey]);
}
